/**
 * Addresses in Canada, the United States and anywhere else.
 *
 * SaleFish began in Ontario and every form showed it: "Province" labels,
 * "M5V 1A1" placeholders, a postal-code tidier that only knew A9A 9A9, a
 * worksheet that gave a UK buyer the list of Canadian provinces. With
 * projects in Florida and Texas and buyers from anywhere, the rules live
 * here once and every form and display reads them:
 *
 *   - a country is read from whatever a row holds ("Canada", "CA", "USA",
 *     "United States", "us") and compared as an ISO code;
 *   - a province or state is offered from a list in Canada and the US and
 *     typed freely anywhere else;
 *   - a postal code is tidied and checked by the country it belongs to
 *     (M5V 3L9, 33131, 33131-4402), and its label says what it is called
 *     there ("Postal code", "ZIP code");
 *   - tables keep the shape they already store (contacts hold country names,
 *     users ISO codes): these read both, so nothing needed migrating.
 */

import { msg, type Msg } from "./i18n";

export type CountryCode = string;

/** Provinces and territories, as Canada Post abbreviates them. */
export const CA_REGIONS: { code: string; name: string }[] = [
  { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" }, { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" }, { code: "NL", name: "Newfoundland and Labrador" }, { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" }, { code: "NU", name: "Nunavut" }, { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" }, { code: "QC", name: "Quebec" }, { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

/** The fifty states, DC and Puerto Rico, as USPS abbreviates them. */
export const US_REGIONS: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" }, { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const COUNTRY_ALIASES: Record<string, CountryCode> = {
  CA: "CA", CAN: "CA", CANADA: "CA",
  US: "US", USA: "US", "U.S.": "US", "U.S.A.": "US", "UNITED STATES": "US", "UNITED STATES OF AMERICA": "US", AMERICA: "US",
  UK: "GB", GB: "GB", "UNITED KINGDOM": "GB", "GREAT BRITAIN": "GB", ENGLAND: "GB",
};

/**
 * Whatever a row holds as a country → its ISO code ("Canada" → "CA",
 * "us" → "US"). Any other two-letter value is taken as a code; an unknown
 * name comes back null rather than guessed.
 */
export function countryCode(value: string | null | undefined): CountryCode | null {
  const v = String(value ?? "").trim().toUpperCase();
  if (!v) return null;
  if (COUNTRY_ALIASES[v]) return COUNTRY_ALIASES[v];
  return /^[A-Z]{2}$/.test(v) ? v : null;
}

/** A province or state, by any spelling, in a country: "Ontario" → "ON", "fl" → "FL". Null when it isn't one. */
export function regionCode(value: string | null | undefined, country?: string | null): string | null {
  const v = String(value ?? "").trim();
  if (!v) return null;
  const cc = countryCode(country);
  const lists = cc === "CA" ? [CA_REGIONS] : cc === "US" ? [US_REGIONS] : [CA_REGIONS, US_REGIONS];
  const up = v.toUpperCase();
  for (const list of lists) {
    const hit = list.find((r) => r.code === up || r.name.toUpperCase() === up || (up === "QUÉBEC" && r.code === "QC"));
    if (hit) return hit.code;
  }
  return null;
}

/** The regions a picker offers for a country, or null where the field is free text. */
export function regionsFor(country: string | null | undefined): { code: string; name: string }[] | null {
  const cc = countryCode(country);
  return cc === "CA" ? CA_REGIONS : cc === "US" ? US_REGIONS : null;
}

/** "ON" → "Ontario", "FL" → "Florida"; anything else as written. */
export function regionName(value: string | null | undefined, country?: string | null): string {
  const code = regionCode(value, country);
  if (!code) return String(value ?? "").trim();
  return [...CA_REGIONS, ...US_REGIONS].find((r) => r.code === code)?.name ?? code;
}

/** A Canadian postal code or a US ZIP; null when the value looks like neither. */
export function postalKind(value: string): "CA" | "US" | null {
  const compact = value.replace(/\s+/g, "").toUpperCase();
  if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) return "CA";
  if (/^\d{5}(-?\d{4})?$/.test(compact)) return "US";
  return null;
}

/**
 * A postal code in its country's written form: "m5v3l9" → "M5V 3L9",
 * "331314402" → "33131-4402". The country decides when known; otherwise the
 * shape does. Anything else is left as typed (trimmed and upper-cased only
 * where that is the country's convention), never mangled.
 */
export function tidyPostalCode(raw: string, country?: string | null): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const cc = countryCode(country) ?? postalKind(trimmed);
  const compact = trimmed.replace(/\s+/g, "").toUpperCase();
  if (cc === "CA" && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) return `${compact.slice(0, 3)} ${compact.slice(3)}`;
  if (cc === "US") {
    const digits = compact.replace(/-/g, "");
    if (/^\d{5}$/.test(digits)) return digits;
    if (/^\d{9}$/.test(digits)) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return cc === "CA" || cc === "GB" ? trimmed.toUpperCase() : trimmed;
}

/** Whether a postal code fits its country. Blank is fine (the field is optional); other countries aren't judged. */
export function isValidPostalCode(raw: string, country?: string | null): boolean {
  const v = raw.trim();
  if (!v) return true;
  const cc = countryCode(country);
  if (cc === "CA") return postalKind(v) === "CA";
  if (cc === "US") return postalKind(v) === "US";
  return v.length <= 12;
}

export const POSTAL_LABEL = {
  CA: msg("addrPostalCode", "Postal Code"),
  US: msg("addrZipCode", "ZIP Code"),
  any: msg("addrPostalOrZip", "Postal or ZIP Code"),
} satisfies Record<string, Msg>;

export const REGION_LABEL = {
  CA: msg("addrProvince", "Province"),
  US: msg("addrState", "State"),
  any: msg("addrProvinceOrState", "Province or State"),
} satisfies Record<string, Msg>;

const pick = <T>(table: { CA: T; US: T; any: T }, country: string | null | undefined): T => {
  const cc = countryCode(country);
  return cc === "CA" ? table.CA : cc === "US" ? table.US : table.any;
};

/** "Postal code" in Canada, "ZIP code" in the US, "Postal or ZIP code" when the country isn't set. */
export const postalLabel = (country?: string | null) => pick(POSTAL_LABEL, country);
/** "Province", "State", or "Province or state". */
export const regionLabel = (country?: string | null) => pick(REGION_LABEL, country);
/** A placeholder in the country's own shape, never a real Toronto address. */
export const postalPlaceholder = (country?: string | null) => pick({ CA: "A1A 1A1", US: "12345", any: "" }, country);

/**
 * One line, the way an envelope reads in either country:
 * "200 Bay Street, Toronto, ON M5J 2J2" or "1200 Brickell Ave, Miami, FL 33131".
 * The country is added only when it isn't the one the reader is in (`home`).
 */
export function formatAddressLine(
  a: { street?: string | null; line1?: string | null; line2?: string | null; city?: string | null; region?: string | null; state?: string | null; province?: string | null; postalCode?: string | null; country?: string | null },
  home: string | null = "CA",
): string {
  const country = countryCode(a.country);
  const region = a.region ?? a.state ?? a.province ?? "";
  const code = regionCode(region, a.country) ?? String(region).trim();
  const tail = [code, a.postalCode ? tidyPostalCode(a.postalCode, a.country) : ""].filter(Boolean).join(" ");
  const parts = [a.street ?? a.line1, a.line2, a.city, tail].map((s) => String(s ?? "").trim()).filter(Boolean);
  if (a.country && country !== countryCode(home)) parts.push(String(a.country).trim());
  return parts.join(", ");
}
